import "./FoodstuffSuggestions.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";

import foodstuffService from "../../services/foodstuff-service";
import TextInput from "../../components/TextInput/TextInput";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import { GlobalContext } from "../GlobalContext";
import { useLocation } from "react-router-dom";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import { ColumnHeader, ColumnValue, DefaultTableFooter } from "../../components/DefaultTable/DefaultTable";
import { formatToDayMonthYearDateTime } from "../../utils/date-converter";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import DefaultDropdown from "../../components/DefaultDropdown/DefaultDropdown";
import NotificationBar from "../../components/NotificationBar/NotificationBar";
import NewFoodstuffModal from "../../components/NewFoodstuffModal/NewFoodstuffModal";
import { useIsMobile } from "../../hooks/useMediaQuery";
import MoreHorizontalIcon from "../../assets/imgs/more-horizontal-icon.svg";

const NAME_COLLAPSE_MAX_LENGTH = 50;

const truncateName = (name, maxLength) => {
  if (!name || name.length <= maxLength) {
    return name;
  }

  const slice = name.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(" ");

  if (lastSpace > 0) {
    return `${slice.slice(0, lastSpace)}...`;
  }

  return `${slice}...`;
};

const FoodstuffSuggestions = () => {
  const isMobile = useIsMobile();
  const [foodstuffSuggestionsResponse, setFoodstuffSuggestionsResponse] = useState({});
  const [foodstuffSuggestions, setFoodstuffSuggestions] = useState([]);
  const [currentFoodstuffSuggestionsPage, setCurrentFoodstuffSuggestionsPage] = useState();
  const searchParams = new URLSearchParams(useLocation().search);
  const foodstuffSuggestionsPageParam = searchParams?.get("currentPage");
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectComment, setRejectComment] = useState("");
  const [showNotificationBar, setShowNotificationBar] = useState(false);
  const [showNewFoodstuffModal, setShowNewFoodstuffModal] = useState(false);
  const [suggestionToAdd, setSuggestionToAdd] = useState(null);
  const [expandedNameRowId, setExpandedNameRowId] = useState(null);

  const columnsWidth = {
    NAME: 2.6,
    BRAND: 1.8,
    DATE: 1.8,
    REQUESTER: 1.8,
    IMAGE: 0.8,
    ACTIONS: 1.8,
  }

  const fetchFoodstuffSuggestions = useCallback(
    async (page) => {
      if (page < 0) {
        fetchFoodstuffSuggestions(0);
        return;
      }

      setIsLoading(true);
      setCurrentFoodstuffSuggestionsPage(page + 1);
      try {
        const foodstuffSuggestionsResponse = await foodstuffService.getFoodstuffSuggestions(
          page,
          7,
        );
        setFoodstuffSuggestionsResponse(foodstuffSuggestionsResponse);
        setFoodstuffSuggestions(foodstuffSuggestionsResponse.content);

        if (page > foodstuffSuggestionsResponse.total_pages) {
          fetchFoodstuffSuggestions(foodstuffSuggestionsResponse.total_pages - 1);
        }
      } catch (error) {
        console.log("Erro ao buscar sugestões de alimentos", error);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading]
  );

  const formatFoodstuffSuggestionName = (name) => {
    if (!name) {
      return name;
    }
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  const rejectReasonOptions = [
    { label: "Já existe um alimento equivalente na base", value: "ALREADY_EXISTS" },
    { label: "Nome muito genérico ou impreciso", value: "GENERIC_OR_IMPRECISE" },
    { label: "Informação insuficiente", value: "INSUFFICIENT_INFORMATION" },
    { label: "Alimento fora do escopo", value: "OUT_OF_SCOPE" },
    { label: "Duplicado", value: "DUPLICATED" },
    { label: "Outro motivo", value: "OTHER" },
  ];

  const handleOpenRejectModal = (foodstuffSuggestion) => {
    setSelectedSuggestion(foodstuffSuggestion);
    setRejectReason("");
    setRejectComment("");
    setShowRejectModal(true);
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setSelectedSuggestion(null);
    setRejectReason("");
    setRejectComment("");
  };

  const handleConfirmReject = async () => {
    if (!rejectReason) {
      return;
    }

    // Se o motivo for "Outro", o comentário é obrigatório
    if (rejectReason === "OTHER" && !rejectComment.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      await foodstuffService.rejectFoodstuffSuggestion(
        selectedSuggestion.id,
        rejectComment || null,
        rejectReason
      );
      await fetchFoodstuffSuggestions(currentFoodstuffSuggestionsPage - 1);
      handleCloseRejectModal();
      setShowNotificationBar(true);
    } catch (error) {
      console.log("Erro ao rejeitar sugestão", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isConfirmDisabled = () => {
    if (!rejectReason) {
      return true;
    }
    // Se o motivo for "Outro", o comentário é obrigatório
    if (rejectReason === "OTHER" && !rejectComment.trim()) {
      return true;
    }
    return false;
  };

  const buildSuggestionQueryParams = (suggestion) =>
    `foodstuffSuggestionId=${suggestion.id}&name=${encodeURIComponent(suggestion.foodstuff_name || "")}`;

  const handleOpenAddModal = (suggestion) => {
    setSuggestionToAdd(suggestion);
    setShowNewFoodstuffModal(true);
  };

  const handleCloseAddModal = () => {
    setShowNewFoodstuffModal(false);
    setSuggestionToAdd(null);
  };

  useEffect(() => {
    document.title = "Sugestões de alimentos";
    setSelectedScreen("foodstuff-suggestions");
    const pageFromParam = foodstuffSuggestionsPageParam
      ? Number(foodstuffSuggestionsPageParam) - 1
      : 0;

    fetchFoodstuffSuggestions(pageFromParam);
  }, [foodstuffSuggestionsPageParam, fetchFoodstuffSuggestions, setSelectedScreen]);

  return (
    <>
      <div className="right-side">
        <div className="right-side__content">
          <div className="right-side__content__header">
            <h1>Solicitações de alimentos {foodstuffSuggestionsResponse?.total_elements ? `(${foodstuffSuggestionsResponse?.total_elements})` : ""}</h1>
          </div>
          <div className="foodstuffSuggestions">
            <div className="foodstuffSuggestions__content">
              {foodstuffSuggestionsResponse?.total_elements > 0 && (
                <div className="default-table foodstuffSuggestions__table">
                  {isMobile ? (
                    <div className="foodstuffSuggestions__mobile-list">
                      {foodstuffSuggestions.map((foodstuffSuggestion) => (
                        <Dropdown
                          key={foodstuffSuggestion.id}
                          className="foodstuffSuggestion__mobile-row-dropdown"
                        >
                          <Dropdown.Toggle
                            as="button"
                            type="button"
                            className="foodstuffSuggestion__mobile-row"
                          >
                            <div className="foodstuffSuggestion__mobile-row__info">
                              <p className="foodstuffSuggestion__mobile-row__name">
                                {formatFoodstuffSuggestionName(foodstuffSuggestion.foodstuff_name)}
                              </p>
                              <p
                                className={`foodstuffSuggestion__mobile-row__brand ${
                                  foodstuffSuggestion?.brand_name
                                    ? ""
                                    : "foodstuffSuggestion__mobile-row__brand--not-appliable"
                                }`}
                              >
                                {foodstuffSuggestion?.brand_name || "Não aplicável"}
                              </p>
                              {foodstuffSuggestion?.has_images && (
                                <span className="foodstuffSuggestion__mobile-row__image-badge">
                                  Com imagem
                                </span>
                              )}
                            </div>
                            <img
                              src={MoreHorizontalIcon}
                              alt=""
                              className="foodstuffSuggestion__mobile-row__icon"
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            align="end"
                            bsPrefix="dropdown-menu foodstuffSuggestion__mobile-row__menu"
                          >
                            <Dropdown.Item
                              className="foodstuffSuggestion__mobile-row__menu__item"
                              onClick={() => handleOpenAddModal(foodstuffSuggestion)}
                            >
                              Adicionar
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="foodstuffSuggestion__mobile-row__menu__item foodstuffSuggestion__mobile-row__menu__item--delete"
                              onClick={() => handleOpenRejectModal(foodstuffSuggestion)}
                            >
                              Excluir
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      ))}
                    </div>
                  ) : (
                    <div className="default-table--preserve-rows foodstuffSuggestions__table__desktop">
                      <div className="foodstuffSuggestions__table__scroll">
                        <div>
                          <div className="table__header">
                            <ColumnHeader title="Nome" column="NAME" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Marca" column="BRAND" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Data e hora" column="DATE" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Solicitante" column="REQUESTER" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Imagem" column="IMAGE" columnsWidth={columnsWidth} />
                            <ColumnHeader title="Ações" column="ACTIONS" columnsWidth={columnsWidth} />
                          </div>
                          <div className="table__content">
                            {foodstuffSuggestions.map((foodstuffSuggestion) => {
                              const formattedName = formatFoodstuffSuggestionName(foodstuffSuggestion.foodstuff_name);
                              const isLongName = formattedName && formattedName.length > NAME_COLLAPSE_MAX_LENGTH;
                              const isExpanded = expandedNameRowId === foodstuffSuggestion.id;

                              return (
                                <div
                                  className={`table__row ${isExpanded ? "table__row--expanded" : ""}`}
                                  key={foodstuffSuggestion.id}
                                >
                                  <ColumnValue
                                    column="NAME"
                                    columnsWidth={columnsWidth}
                                    className={`foodstuffSuggestion__name ${isLongName && !isExpanded ? "foodstuffSuggestion__name--collapsed" : ""}`}
                                  >
                                    <span className="foodstuffSuggestion__name__text">
                                      {isLongName && !isExpanded
                                        ? truncateName(formattedName, NAME_COLLAPSE_MAX_LENGTH)
                                        : formattedName}
                                    </span>
                                    {isLongName && (
                                      <button
                                        type="button"
                                        className="foodstuffSuggestion__name__toggle"
                                        onClick={() => setExpandedNameRowId(isExpanded ? null : foodstuffSuggestion.id)}
                                      >
                                        {isExpanded ? "Ver menos" : "Ver mais"}
                                      </button>
                                    )}
                                  </ColumnValue>
                                  <ColumnValue column="BRAND" columnsWidth={columnsWidth} className={foodstuffSuggestion?.brand_name ? "" : "foodstuffSuggestion__brand--not-appliable"}>
                                    {foodstuffSuggestion?.brand_name ? foodstuffSuggestion?.brand_name : "Não aplicável"}
                                  </ColumnValue>
                                  <ColumnValue column="DATE" columnsWidth={columnsWidth}>
                                    {formatToDayMonthYearDateTime(foodstuffSuggestion.created_at)}
                                  </ColumnValue>
                                  <ColumnValue
                                    column="REQUESTER"
                                    columnsWidth={columnsWidth}
                                    className={foodstuffSuggestion?.created_by_name ? "" : "foodstuffSuggestion__brand--not-appliable"}
                                  >
                                    {foodstuffSuggestion?.created_by_name || "—"}
                                  </ColumnValue>
                                  <ColumnValue
                                    column="IMAGE"
                                    columnsWidth={columnsWidth}
                                    className={foodstuffSuggestion?.has_images ? "" : "foodstuffSuggestion__brand--not-appliable"}
                                  >
                                    {foodstuffSuggestion?.has_images ? "Sim" : "Não"}
                                  </ColumnValue>
                                  <ColumnValue column="ACTIONS" columnsWidth={columnsWidth} className="foodstuffSuggestion__actions-column">
                                    <div className="foodstuffSuggestion__actions">
                                      <DefaultButton title="Excluir" onClick={() => handleOpenRejectModal(foodstuffSuggestion)} type="secondary" size="small" textStyle="foodstuffSuggestion__action"/>
                                      <DefaultButton title="Adicionar" onClick={() => handleOpenAddModal(foodstuffSuggestion)} size="small" textStyle="foodstuffSuggestion__action"/>
                                    </div>
                                  </ColumnValue>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <DefaultTableFooter>
                    <NumericPagination
                      currentPage={currentFoodstuffSuggestionsPage || 1}
                      totalPages={foodstuffSuggestionsResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => {
                        fetchFoodstuffSuggestions(page);
                      }}
                    />
                  </DefaultTableFooter>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <DefaultModal
        title="Excluir sugestão de alimento"
        showModal={showRejectModal}
        onClose={handleCloseRejectModal}
        className="reject-suggestion-modal"
      >
        <div className="reject-suggestion-modal__content">
          <div className="reject-suggestion-modal__content__description">
            <p>Selecione o motivo para registrar essa decisão.</p>
          </div>
          <div className="reject-suggestion-modal__form">
            <div className="reject-suggestion-modal__form__field">
              <DefaultDropdown
                placeholder="Selecione o motivo"
                value={rejectReasonOptions.find(opt => opt.value === rejectReason)?.label}
                options={rejectReasonOptions.map(opt => opt.label)}
                onSelect={(label) => {
                  const option = rejectReasonOptions.find(opt => opt.label === label);
                  setRejectReason(option?.value || "");
                }}
              />
            </div>
            <div className="reject-suggestion-modal__form__field">
              <TextInput
                label={rejectReason === "OTHER" ? "Comentário" : "Comentário (opcional)"}
                placeholder="Digite um comentário..."
                value={rejectComment}
                onChangeText={setRejectComment}
                invalid={rejectReason === "OTHER" && !rejectComment.trim()}
                invalidMessage={rejectReason === "OTHER" && !rejectComment.trim() ? "O comentário é obrigatório quando o motivo é 'Outro'" : ""}
              />
            </div>
          </div>
          <div className="reject-suggestion-modal__actions">
            <DefaultButton
              title="Excluir"
              onClick={handleConfirmReject}
              disabled={isConfirmDisabled()}
            />
          </div>
        </div>
      </DefaultModal>
      <NotificationBar
        title="Sugestão excluída com sucesso!"
        durationInSeconds={5}
        show={showNotificationBar}
        setShow={setShowNotificationBar}
      />
      <NewFoodstuffModal
        show={showNewFoodstuffModal}
        onClose={handleCloseAddModal}
        suggestion={suggestionToAdd}
        extraQueryParams={
          suggestionToAdd ? buildSuggestionQueryParams(suggestionToAdd) : undefined
        }
      />
    </>
  );
};

export default FoodstuffSuggestions;
